import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircleQuestion, Lock, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const topics = [
  "Grief & Loss",
  "Faith & Meaning",
  "Caregiving",
  "Relationships",
  "Emotional Stress",
  "Workplace Burnout",
  "Other",
];

const SubmitQuestion = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    topic: "",
    question: "",
    anonymous: false,
  });
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.question.trim() || form.question.trim().length < 5) {
      toast.error("Please share a bit more so Chaplain Derrick can respond well.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        question: form.question.trim(),
        anonymous: form.anonymous,
        topic: form.topic || null,
        name: form.anonymous ? null : form.name.trim() || null,
        email: form.anonymous ? null : form.email.trim() || null,
      };
      await axios.post(`${API}/questions`, payload);
      toast.success("Thank you. Your question has been received with care.");
      setForm({ name: "", email: "", topic: "", question: "", anonymous: false });
    } catch (err) {
      toast.error(
        err?.response?.data?.detail?.[0]?.msg ||
          "Something went wrong sending your question. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="submit"
      data-testid="submit-question-section"
      className="py-20 lg:py-28 bg-[#F8FAFC]"
    >
      <div className="max-w-3xl mx-auto px-6 sm:px-8">
        <div className="text-center">
          <span className="text-xs uppercase tracking-[0.22em] font-semibold text-[#D97706]">
            Submit a Question
          </span>
          <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900 leading-tight">
            Ask anything you&apos;ve been quietly carrying.
          </h2>
          <p className="mt-5 text-slate-600 text-base sm:text-lg leading-relaxed">
            Questions are answered with care on the podcast or by email.
            You&apos;re welcome to submit anonymously.
          </p>
        </div>

        <form
          onSubmit={submit}
          data-testid="submit-question-form"
          className="mt-10 bg-white border border-slate-100 rounded-2xl p-6 sm:p-8 shadow-sm"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-slate-600 uppercase tracking-widest">
                Your name <span className="text-slate-400 normal-case">(optional)</span>
              </label>
              <input
                type="text"
                data-testid="question-name-input"
                value={form.name}
                onChange={(e) => update("name", e.target.value)}
                disabled={form.anonymous}
                placeholder="First name is enough"
                className="mt-2 w-full rounded-lg border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#0369A1] focus:ring-2 focus:ring-[#0369A1]/15 outline-none px-4 py-3 text-sm disabled:opacity-50"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 uppercase tracking-widest">
                Email <span className="text-slate-400 normal-case">(optional)</span>
              </label>
              <input
                type="email"
                data-testid="question-email-input"
                value={form.email}
                onChange={(e) => update("email", e.target.value)}
                disabled={form.anonymous}
                placeholder="you@example.com"
                className="mt-2 w-full rounded-lg border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#0369A1] focus:ring-2 focus:ring-[#0369A1]/15 outline-none px-4 py-3 text-sm disabled:opacity-50"
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="text-xs font-medium text-slate-600 uppercase tracking-widest">
              Topic
            </label>
            <select
              data-testid="question-topic-select"
              value={form.topic}
              onChange={(e) => update("topic", e.target.value)}
              className="mt-2 w-full rounded-lg border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#0369A1] focus:ring-2 focus:ring-[#0369A1]/15 outline-none px-4 py-3 text-sm"
            >
              <option value="">Choose a topic (optional)</option>
              {topics.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>

          <div className="mt-4">
            <label className="text-xs font-medium text-slate-600 uppercase tracking-widest">
              Your question
            </label>
            <textarea
              required
              data-testid="question-text-input"
              value={form.question}
              onChange={(e) => update("question", e.target.value)}
              rows={5}
              placeholder="Share as much or as little as you'd like..."
              className="mt-2 w-full rounded-lg border border-slate-200 bg-slate-50 focus:bg-white focus:border-[#0369A1] focus:ring-2 focus:ring-[#0369A1]/15 outline-none px-4 py-3 text-sm resize-none"
            />
          </div>

          <label
            data-testid="question-anonymous-toggle"
            className="mt-4 inline-flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none"
          >
            <input
              type="checkbox"
              checked={form.anonymous}
              onChange={(e) => update("anonymous", e.target.checked)}
              className="h-4 w-4 rounded border-slate-300 text-[#0369A1] focus:ring-[#0369A1]"
            />
            <Lock size={14} className="text-slate-400" />
            Submit anonymously
          </label>

          <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <p className="text-xs text-slate-500 max-w-md">
              By submitting, you agree your question may be answered (anonymized) on the
              podcast or by email.
            </p>
            <button
              type="submit"
              disabled={loading}
              data-testid="question-submit-button"
              className="inline-flex items-center gap-2 bg-[#0369A1] hover:bg-[#0284C7] disabled:opacity-60 text-white rounded-full px-6 py-3 text-sm font-medium transition-colors"
            >
              {loading ? (
                "Sending..."
              ) : (
                <>
                  <Send size={14} /> Send my question
                </>
              )}
              <MessageCircleQuestion size={14} className="ml-1 opacity-80" />
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default SubmitQuestion;
